import React, { useState, useRef } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { env_manual_url } from "serverEnv";
import { Page } from "store/manual/manuals/pages/types.d";
import { color, buttonBaseStyle } from "components/mixins";
import { pc } from "components/mixins/mediaQuery";
import { useParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { generateTailwindHtmlDoc } from "utils/tailwindHtmlGenerator";
import { html as beautifyHtml } from "js-beautify";

import { setPageDetailAction } from "store/manual/manuals/pages/actions";
import { manualPageDetailOnEditSelector } from "store/manual/manuals/pages/selectors";

const TailwindHTMLEditor = React.memo(
  (_props: {
    storedPageDetail: Page;
    pageTagIds: { id: number }[];
    pageTitle: string;
  }) => {
    const dispatch = useDispatch();
    const token = useSelector((state: any) => state.session.token);
    const storedPage = useSelector(manualPageDetailOnEditSelector);
    const { manualId } = useParams<{
      manualId: string;
      topicId: string;
      pageId: string;
    }>();

    const [htmlContent, setHtmlContent] = useState(
      storedPage?.content_draft || ""
    );
    const [isUploading, setIsUploading] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setHtmlContent(value);
      dispatch(
        setPageDetailAction({
          ...storedPage,
          content_draft: value
        })
      );
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.ctrlKey && e.shiftKey && e.key === "F") {
        e.preventDefault();
        handleFormatClick();
      }
    };

    const uploadImage = async (file: File): Promise<string> => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("manual_id", manualId);
      formData.append("file_name", file.name);
      formData.append("alt", file.name);

      const response = await axios.post(
        `${env_manual_url}/contents/image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      if (response.data && response.data.path) {
        return response.data.path;
      }
      throw new Error("画像のアップロードに失敗しました");
    };

    const insertImageAtCursor = (imageUrl: string, altText: string) => {
      if (textareaRef.current) {
        const textarea = textareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const imageHtml = `<img src="${imageUrl}" alt="${altText}" class="w-full max-w-2xl mx-auto my-4" />`;

        const newText =
          text.substring(0, start) + imageHtml + text.substring(end);
        setHtmlContent(newText);

        // カーソル位置を画像タグの後に設定
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd =
            start + imageHtml.length;
          textarea.focus();
        }, 0);
      }
    };

    const onDrop = async (acceptedFiles: File[]) => {
      setIsUploading(true);
      try {
        for (const file of acceptedFiles) {
          if (file.type.startsWith("image/")) {
            const imageUrl = await uploadImage(file);
            insertImageAtCursor(imageUrl, file.name);
          }
        }
      } catch (error) {
        console.error("画像アップロードエラー:", error);
        alert("画像のアップロードに失敗しました");
      } finally {
        setIsUploading(false);
      }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      accept: {
        "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"]
      },
      noClick: true
    });

    const handleImageButtonClick = () => {
      fileInputRef.current?.click();
    };

    const handleFormatClick = () => {
      try {
        const formatted = beautifyHtml(htmlContent, {
          indent_size: 2,
          indent_char: " ",
          max_preserve_newlines: 2,
          preserve_newlines: true,
          indent_scripts: "normal",
          end_with_newline: false,
          wrap_line_length: 0,
          indent_inner_html: false
        });

        setHtmlContent(formatted);
        dispatch(
          setPageDetailAction({
            ...storedPage,
            content_draft: formatted
          })
        );
      } catch (error) {
        console.error("HTMLフォーマットエラー:", error);
        alert(
          "HTMLのフォーマットに失敗しました。HTMLの構文を確認してください。"
        );
      }
    };

    const handleFileInputChange = async (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const files = e.target.files;
      if (files) {
        await onDrop(Array.from(files));
      }
    };

    // プレビュー用のHTML生成
    const previewHtmlDoc = generateTailwindHtmlDoc({
      content: htmlContent,
      title: "プレビュー",
      includeBodyPadding: true
    });

    return (
      <EditorContainer>
        <EditorBody>
          <EditorSection {...getRootProps()}>
            <SectionHeader>
              <SectionTitle>HTML編集</SectionTitle>
              <ButtonGroup>
                <ActionButton onClick={handleFormatClick}>
                  フォーマット
                </ActionButton>
                <ActionButton onClick={handleImageButtonClick}>
                  画像を挿入
                </ActionButton>
              </ButtonGroup>
            </SectionHeader>

            <TextAreaWrapper isDragActive={isDragActive}>
              <StyledTextArea
                ref={textareaRef}
                value={htmlContent}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="HTMLコードを入力してください。TailwindCSSのクラスが使用できます。"
              />
              <input {...getInputProps()} />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handleFileInputChange}
              />
              {isDragActive && (
                <DropOverlay>
                  <DropText>画像をドロップしてアップロード</DropText>
                </DropOverlay>
              )}
              {isUploading && (
                <UploadingOverlay>
                  <UploadingText>アップロード中...</UploadingText>
                </UploadingOverlay>
              )}
            </TextAreaWrapper>
          </EditorSection>

          <PreviewSection>
            <SectionHeader>
              <SectionTitle>プレビュー</SectionTitle>
            </SectionHeader>
            <PreviewFrame
              srcDoc={previewHtmlDoc}
              title="プレビュー"
              sandbox="allow-scripts"
            />
          </PreviewSection>
        </EditorBody>

        <HelpText>
          <strong>使用例:</strong>
          <br />
          &lt;h1 class="text-3xl font-bold text-gray-800"&gt;見出し&lt;/h1&gt;
          <br />
          &lt;p class="text-lg text-gray-600"&gt;本文テキスト&lt;/p&gt;
          <br />
          &lt;div class="bg-blue-100 p-4 rounded-lg"&gt;ボックス&lt;/div&gt;
          <br />
          <strong>ショートカット:</strong> Ctrl+Shift+F (フォーマット)
        </HelpText>
      </EditorContainer>
    );
  }
);

export default TailwindHTMLEditor;

// Styled Components
const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-height: calc(100vh - 200px);
`;

const EditorBody = styled.div`
  display: flex;
  flex: 1;
  gap: 0;
  padding: 0;
  min-height: 600px;
  overflow: hidden;

  ${pc`
    flex-direction: row;
  `}

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const EditorSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
  border-right: 1px solid ${color.divide_border};
`;

const PreviewSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
  background-color: #fafafa;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  ${buttonBaseStyle}
  padding: 6px 16px;
  background-color: ${color.primary};
  color: white;
  border-radius: 4px;
  font-size: 14px;
  transition: background-color 0.2s;
  opacity: 1;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.3;
  }
`;

const TextAreaWrapper = styled.div<{ isDragActive: boolean }>`
  position: relative;
  flex: 1;
  border: 2px dashed
    ${props => (props.isDragActive ? color.primary : color.divide_border)};
  border-radius: 8px;
  transition: border-color 0.2s;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 16px;
  border: none;
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  font-size: 14px;
  line-height: 1.6;
  resize: none;
  outline: none;
  background-color: #f9fafb;
`;

const PreviewFrame = styled.iframe`
  flex: 1;
  border: 1px solid ${color.divide_border};
  border-radius: 8px;
  background-color: white;
`;

const DropOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(59, 130, 246, 0.1);
  border-radius: 8px;
  pointer-events: none;
`;

const DropText = styled.p`
  font-size: 18px;
  font-weight: 500;
  color: ${color.primary};
`;

const UploadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
`;

const UploadingText = styled.p`
  font-size: 16px;
  color: ${color.text_secondary};
`;

const HelpText = styled.div`
  padding: 12px 30px;
  margin: 0;
  font-size: 12px;
  color: ${color.text_secondary};
  background-color: #f9fafb;
  border-top: 1px solid ${color.divide_border};
  line-height: 1.5;
  font-family: "Consolas", "Monaco", "Courier New", monospace;

  strong {
    display: inline;
    margin-right: 8px;
    font-weight: 600;
    color: ${color.black};
  }

  br {
    display: none;
  }
`;
